import { element, showMessage } from './publicUi.js';
import { getLocale } from '../services/i18n.js';

const roles = [['MEMBER', 'Участник'], ['RESEARCHER', 'Исследователь'], ['SENIOR_RESEARCHER', 'Старший исследователь'], ['SYSADMIN', 'Системный администратор']];
const statuses = [['ACTIVE', 'Активен'], ['BLOCKED', 'Заблокирован']];

function selectValue(select, value) {
  [...select.options].find(option => option.value === value)?.setAttribute('selected', '');
}

export class UserAccessEditor {
  constructor({ item, save, revoke, remove, saved, denied, cancel, checkList }) {
    Object.assign(this, { item, save, revoke, remove, saved, denied, cancel, checkList });
  }
  render() {
    this.form = element('form', 'spec-box post-editor');
    this.form.append(element('h2', 'spec-title', this.item.username),
      element('p', 'profile-text', `${this.item.email} · ${this.item.uid}`),
      element('p', 'profile-text', `Email подтверждён: ${this.item.emailVerifiedAt ? new Date(this.item.emailVerifiedAt).toLocaleString(getLocale()) : 'нет'}`),
      element('p', 'profile-text', `Регистрация: ${new Date(this.item.createdAt).toLocaleString(getLocale())}`));
    this.role = this.addSelect('role', 'Роль пользователя', roles, this.item.role);
    this.status = this.addSelect('status', 'Статус пользователя', statuses, this.item.status);
    this.submitButton = element('button', 'filter-btn', 'Сохранить доступ');
    this.cancelButton = element('button', 'filter-btn', 'Отменить и вернуться к списку'); this.cancelButton.type = 'button';
    this.cancelButton.addEventListener('click', () => { if (!this.busy && !this.disposed) this.cancel(); });
    this.feedback = element('div'); this.feedback.setAttribute('role', 'status');
    this.checkListButton = element('button', 'filter-btn', 'Вернуться к списку и проверить результат'); this.checkListButton.type = 'button'; this.checkListButton.hidden = true;
    this.checkListButton.addEventListener('click', () => { if (!this.busy && !this.disposed) void this.checkList?.(); });
    this.form.append(this.submitButton, this.cancelButton, this.feedback, this.checkListButton);

    this.revokeButton = element('button', 'filter-btn', 'Завершить все сессии'); this.revokeButton.type = 'button';
    this.revokeConfirmation = this.confirmation(
      `Завершить все текущие сессии пользователя «${this.item.username}»? Пользователю потребуется войти снова.`,
      'Да, завершить все сессии', this.revokeButton, () => this.revokeSessions());
    this.deleteButton = element('button', 'filter-btn admin-delete-button', 'Удалить пользователя'); this.deleteButton.type = 'button';
    this.deleteConfirmation = this.confirmation(
      `Удалить пользователя «${this.item.username}»? Учётная запись будет анонимизирована без возможности восстановления.`,
      'Да, удалить пользователя', this.deleteButton, () => this.removeUser());
    this.form.append(this.revokeButton, this.revokeConfirmation.box, this.deleteButton, this.deleteConfirmation.box);
    this.form.addEventListener('submit', event => { event.preventDefault(); void this.submit(); });
    return this.form;
  }
  addSelect(name, label, values, initial) {
    const select = element('select', 'auth-input'); select.name = name; select.setAttribute('aria-label', label);
    for (const [value, text] of values) { const option = element('option', '', text); option.value = value; select.append(option); }
    selectValue(select, initial);
    const caption = element('label', 'auth-form-label', label); caption.append(select); this.form.append(caption);
    return select;
  }
  confirmation(message, confirmText, trigger, action) {
    const box = element('div', 'public-message'); box.hidden = true; box.append(element('p', '', message));
    const confirm = element('button', 'filter-btn', confirmText), back = element('button', 'filter-btn', 'Отмена'); confirm.type = back.type = 'button';
    trigger.addEventListener('click', () => {
      if (this.busy || this.completed || this.disposed || this.uncertain) return;
      this.closeConfirmations(); box.hidden = false; back.focus();
    });
    back.addEventListener('click', () => { if (!this.busy) { box.hidden = true; trigger.focus(); } });
    confirm.addEventListener('click', () => { void action(); }); box.append(confirm, back);
    return { box, confirm, back };
  }
  closeConfirmations() {
    if (this.revokeConfirmation) this.revokeConfirmation.box.hidden = true;
    if (this.deleteConfirmation) this.deleteConfirmation.box.hidden = true;
  }
  setBusy(value) {
    this.busy = value;
    for (const control of this.form.querySelectorAll('select,button')) {
      control.disabled = value || !!this.completed || (!!this.uncertain && control !== this.cancelButton && control !== this.checkListButton);
    }
  }
  async submit() {
    if (this.busy || this.disposed || this.completed || this.uncertain || !this.revokeConfirmation.box.hidden || !this.deleteConfirmation.box.hidden) return;
    if (!roles.some(([value]) => value === this.role.value) || !statuses.some(([value]) => value === this.status.value)) {
      showMessage(this.feedback, 'Выберите допустимые роль и статус пользователя.'); return;
    }
    const change = {};
    if (this.role.value !== this.item.role) change.role = this.role.value;
    if (this.status.value !== this.item.status) change.status = this.status.value;
    if (!Object.keys(change).length) { showMessage(this.feedback, 'Нет изменений для сохранения.'); return; }
    this.setBusy(true); showMessage(this.feedback, 'Сохранение доступа…');
    try {
      const result = await this.save(this.item.id, change); this.completed = true;
      if (!this.disposed) { showMessage(this.feedback, 'Доступ обновлён.'); this.saved(result); }
    } catch (error) { this.handleFailure(error, 'изменения доступа'); }
    finally { this.busy = false; if (!this.disposed) this.setBusy(false); }
  }
  async revokeSessions() {
    if (this.busy || this.disposed || this.completed || this.uncertain || this.revokeConfirmation.box.hidden) return;
    this.setBusy(true); showMessage(this.feedback, 'Завершение сессий…');
    try {
      await this.revoke(this.item.id); this.completed = true;
      if (!this.disposed) { showMessage(this.feedback, 'Все сессии пользователя завершены.'); this.saved(); }
    } catch (error) { this.handleFailure(error, 'отзыва сессий'); }
    finally { this.busy = false; if (!this.disposed) { this.closeConfirmations(); this.setBusy(false); } }
  }
  async removeUser() {
    if (this.busy || this.disposed || this.completed || this.uncertain || this.deleteConfirmation.box.hidden) return;
    this.setBusy(true); showMessage(this.feedback, 'Удаление пользователя…');
    try {
      await this.remove(this.item.id); this.completed = true;
      if (!this.disposed) { showMessage(this.feedback, 'Пользователь удалён.'); this.saved(); }
    } catch (error) {
      if (!this.disposed && error.status === 404) { this.completed = true; showMessage(this.feedback, 'Пользователь уже отсутствует.'); this.saved(); }
      else this.handleFailure(error, 'удаления пользователя');
    } finally { this.busy = false; if (!this.disposed) { this.closeConfirmations(); this.setBusy(false); } }
  }
  handleFailure(error, operation) {
    if (this.disposed) return;
    if (error.status === 401 || (error.status === 403 && error.code !== 'CSRF_INVALID')) { this.denied(error); return; }
    this.uncertain = !error.status || error.status >= 500 || error.code === 'UNAVAILABLE';
    this.checkListButton.hidden = !this.uncertain || !this.checkList;
    showMessage(this.feedback, this.uncertain
      ? `Результат ${operation} неизвестен. Вернитесь к списку и откройте пользователя заново перед следующей операцией.`
      : error.message);
  }
  dispose() { this.disposed = true; }
}
